import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getModels,
  getProviderStatuses,
  getUniversalEndpoints,
} from "../lib/api";
import { useAppStore } from "../lib/store";
import type { ModelInfo, Provider, ProviderStatus } from "../lib/types";

export type ModelLoadKind =
  | "idle"
  | "loading"
  | "ready"
  | "missing_key"
  | "invalid_key"
  | "network_error"
  | "provider_error"
  | "unavailable";

export interface ModelLoadState {
  kind: ModelLoadKind;
  message?: string;
  count: number;
}

const emptyModels = (): Record<Provider, ModelInfo[]> => ({
  openrouter: [],
  gemini: [],
  universal: [],
});

const initialStates = (): Record<Provider, ModelLoadState> => ({
  openrouter: { kind: "idle", count: 0 },
  gemini: { kind: "idle", count: 0 },
  universal: { kind: "idle", count: 0 },
});

function universalModels(
  endpoints: Awaited<ReturnType<typeof getUniversalEndpoints>>,
): ModelInfo[] {
  return endpoints.map((endpoint) => ({
    id: endpoint.id,
    name: `${endpoint.name} · ${endpoint.model}`,
    provider: "universal",
    contextWindow: 0,
    available: true,
    ...(endpoint.inputPrice !== undefined
      ? { inputPrice: endpoint.inputPrice }
      : {}),
    ...(endpoint.outputPrice !== undefined
      ? { outputPrice: endpoint.outputPrice }
      : {}),
    description: endpoint.baseUrl,
    modalities: ["text", "image"],
  }));
}

export function filterModels(
  models: ModelInfo[],
  query: string,
): ModelInfo[] {
  const terms = query
    .trim()
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!terms.length) return models;
  return models.filter((model) => {
    const haystack = `${model.name} ${model.id}`.toLocaleLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

export function useModels() {
  const [models, setModels] = useState<Record<Provider, ModelInfo[]>>(emptyModels);
  const [states, setStates] =
    useState<Record<Provider, ModelLoadState>>(initialStates);
  const [statuses, setStatuses] = useState<
    Partial<Record<"openrouter" | "gemini", ProviderStatus>>
  >({});
  // Mirrors of the state above, so a refresh of one provider can merge against
  // the current catalog without `load` depending on — and being recreated by —
  // every state change it makes.
  const catalogRef = useRef<Record<Provider, ModelInfo[]>>(emptyModels());
  const statusesRef = useRef<Partial<Record<"openrouter" | "gemini", ProviderStatus>>>(
    {},
  );
  const setModelCatalog = useAppStore((state) => state.setModelCatalog);
  const setModelsLoading = useAppStore((state) => state.setModelsLoading);

  const load = useCallback(
    async (only?: Provider) => {
      setModelsLoading(true);
      const providers: Provider[] = only
        ? [only]
        : ["openrouter", "gemini", "universal"];
      setStates((current) => ({
        ...current,
        ...Object.fromEntries(
          providers.map((provider) => [
            provider,
            { ...current[provider], kind: "loading" as const },
          ]),
        ),
      }));

      let nextStatuses = statusesRef.current;
      try {
        const loadedStatuses = await getProviderStatuses();
        nextStatuses = Object.fromEntries(
          loadedStatuses.map((status) => [status.provider, status]),
        );
        setStatuses(nextStatuses);
      } catch {
        // Individual model endpoints still provide precise errors.
      }

      const results = await Promise.all(
        providers.map(async (provider) => {
          try {
            if (provider === "universal") {
              const value = universalModels(await getUniversalEndpoints());
              return {
                provider,
                value,
                state: { kind: "ready" as const, count: value.length },
              };
            }
            const result = await getModels(provider);
            nextStatuses = { ...nextStatuses, [provider]: result.status };
            return {
              provider,
              value: result.models,
              state: {
                kind: "ready" as const,
                count: result.models.length,
                ...(result.models.length
                  ? {}
                  : { message: "Lista modelli vuota." }),
              },
            };
          } catch (caught) {
            const error = caught as Error & {
              status?: number;
              providerStatus?: ProviderStatus;
            };
            if (provider !== "universal" && error.providerStatus) {
              nextStatuses = {
                ...nextStatuses,
                [provider]: error.providerStatus,
              };
            }
            const kind: ModelLoadKind =
              error.status === 401
                ? error.providerStatus?.hasApiKey
                  ? "invalid_key"
                  : "missing_key"
                : error.status === 429 || (error.status && error.status >= 500)
                  ? "provider_error"
                  : error.message.toLowerCase().includes("fetch")
                    ? "network_error"
                    : "unavailable";
            return {
              provider,
              value: [] as ModelInfo[],
              state: { kind, count: 0, message: error.message },
            };
          }
        }),
      );

      // Merged against refs rather than inside a setState updater: React runs
      // updaters during render, so calling the Zustand setter in there pushed a
      // store update mid-render and made every subscriber warn.
      const nextModels = { ...catalogRef.current };
      for (const result of results) nextModels[result.provider] = result.value;
      catalogRef.current = nextModels;
      statusesRef.current = nextStatuses;

      const errors = results
        .filter((result) => result.state.kind !== "ready")
        .map((result) => result.state.message)
        .filter(Boolean)
        .join(" · ");

      setStatuses(nextStatuses);
      setModels(nextModels);
      setStates((current) => {
        const next = { ...current };
        for (const result of results) next[result.provider] = result.state;
        return next;
      });
      setModelCatalog(nextModels, nextStatuses, errors || undefined);
      setModelsLoading(false);
    },
    [setModelCatalog, setModelsLoading],
  );

  useEffect(() => {
    void load();
    // Initial synchronization only; manual refreshes call load explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allModels = useMemo(
    () => [...models.openrouter, ...models.gemini, ...models.universal],
    [models],
  );
  const findModel = (provider: Provider, id: string) =>
    models[provider].find((model) => model.id === id);

  return {
    models,
    allModels,
    findModel,
    loading: Object.values(states).some((state) => state.kind === "loading"),
    states,
    statuses,
    refresh: load,
    refreshUniversal: () => load("universal"),
  };
}
